import { useState } from "react";

interface ClientItem {
  name: string;
  jobs: number;
  revenue: number;
  paid: number;
}

interface ClientRevenueChartProps {
  data: ClientItem[];
}

const CLIENT_COLORS = [
  "#f59e0b", "#d97706", "#b45309", "#92400e",
  "#fbbf24", "#fcd34d", "#f97316", "#ea580c",
];

type SortMode = "revenue" | "collected" | "outstanding" | "jobs";

export default function ClientRevenueChart({ data }: ClientRevenueChartProps) {
  const [sortMode, setSortMode] = useState<SortMode>("revenue");
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const sorted = [...data].sort((a, b) => {
    if (sortMode === "revenue") return b.revenue - a.revenue;
    if (sortMode === "collected") return b.paid - a.paid;
    if (sortMode === "outstanding") return (b.revenue - b.paid) - (a.revenue - a.paid);
    return b.jobs - a.jobs;
  });

  const maxRevenue = Math.max(...sorted.map((c) => c.revenue), 1);
  const totalRevenue = data.reduce((a, b) => a + b.revenue, 0);
  const totalCollected = data.reduce((a, b) => a + b.paid, 0);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-gray-800">Client Revenue Summary</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Top {sorted.length} clients · ₹{(totalRevenue / 100000).toFixed(1)}L total billed
          </p>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {([
            { key: "revenue",     label: "Billed" },
            { key: "collected",   label: "Collected" },
            { key: "outstanding", label: "Outstanding" },
            { key: "jobs",        label: "Jobs" },
          ] as { key: SortMode; label: string }[]).map((s) => (
            <button
              key={s.key}
              onClick={() => setSortMode(s.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                sortMode === s.key ? "bg-white text-amber-700" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-amber-400 opacity-30" />
          Total Billed
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-amber-500" />
          Collected
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-red-300" />
          Outstanding
        </span>
      </div>

      {/* Horizontal bar chart */}
      <div className="space-y-3">
        {sorted.map((client, idx) => {
          const color = CLIENT_COLORS[idx % CLIENT_COLORS.length];
          const billedPct = (client.revenue / maxRevenue) * 100;
          const collectedPct = (client.paid / maxRevenue) * 100;
          const outstanding = client.revenue - client.paid;
          const outstandingPct = (outstanding / maxRevenue) * 100;
          const collectionRate = Math.round((client.paid / (client.revenue || 1)) * 100);
          const isHovered = hoveredIdx === idx;

          return (
            <div
              key={client.name}
              className={`rounded-xl p-3 transition-all cursor-default ${isHovered ? "bg-gray-50" : ""}`}
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {/* Client name row */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: color }}
                  />
                  <span className="text-sm font-semibold text-gray-800 truncate">{client.name}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0">{client.jobs} job{client.jobs !== 1 ? "s" : ""}</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                  <span className="text-xs font-bold text-gray-800">
                    ₹{(client.revenue / 1000).toFixed(0)}K
                  </span>
                  <span
                    className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                      collectionRate === 100
                        ? "bg-amber-50 text-amber-700"
                        : collectionRate > 0
                        ? "bg-amber-50 text-amber-600"
                        : "bg-red-50 text-red-500"
                    }`}
                  >
                    {collectionRate}%
                  </span>
                </div>
              </div>

              {/* Stacked bar */}
              <div className="relative h-5 bg-gray-100 rounded-lg overflow-hidden">
                {/* Billed background */}
                <div
                  className="absolute left-0 top-0 h-full rounded-lg transition-all duration-500"
                  style={{ width: `${billedPct}%`, background: `${color}22` }}
                />
                {/* Collected */}
                <div
                  className="absolute left-0 top-0 h-full rounded-lg transition-all duration-500"
                  style={{ width: `${collectedPct}%`, background: color }}
                />
                {/* Outstanding overlay */}
                {outstanding > 0 && (
                  <div
                    className="absolute top-0 h-full rounded-r-lg transition-all duration-500"
                    style={{
                      left: `${collectedPct}%`,
                      width: `${outstandingPct}%`,
                      background: "#fca5a5",
                    }}
                  />
                )}
              </div>

              {/* Detail row on hover */}
              {isHovered && (
                <div className="mt-2 flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: color }} />
                    Collected: <strong className="text-gray-700 ml-1">₹{client.paid.toLocaleString("en-IN")}</strong>
                  </span>
                  {outstanding > 0 && (
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-300" />
                      Outstanding: <strong className="text-red-500 ml-1">₹{outstanding.toLocaleString("en-IN")}</strong>
                    </span>
                  )}
                  <span className="text-gray-400">
                    {Math.round((client.revenue / totalRevenue) * 100)}% of total revenue
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary footer */}
      <div className="mt-5 pt-4 border-t border-gray-100 grid grid-cols-3 gap-4">
        {[
          {
            label: "Total Billed",
            value: `₹${(totalRevenue / 100000).toFixed(2)}L`,
            color: "text-amber-700",
            icon: "ri-money-rupee-circle-line",
            bg: "bg-amber-50",
          },
          {
            label: "Total Collected",
            value: `₹${(totalCollected / 100000).toFixed(2)}L`,
            color: "text-amber-600",
            icon: "ri-checkbox-circle-line",
            bg: "bg-amber-50",
          },
          {
            label: "Outstanding",
            value: `₹${((totalRevenue - totalCollected) / 100000).toFixed(2)}L`,
            color: "text-red-500",
            icon: "ri-alarm-warning-line",
            bg: "bg-red-50",
          },
        ].map((s) => (
          <div key={s.label} className={`rounded-xl p-3 ${s.bg} text-center`}>
            <div className="w-7 h-7 flex items-center justify-center mx-auto mb-1.5">
              <i className={`${s.icon} text-base ${s.color}`} />
            </div>
            <p className={`text-base font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
